/**
 * The line record (ADR-119 decision 5): the shape every piece of authored text a player reads is written as, a
 * spoken line, a player's choice text, a quest's title, an objective, a log entry or an NPC's name. Each record has
 * an id, `l` and a number, that is never reused or renumbered: the embedding file holds `nextLine`, one past the
 * highest number it has ever used, so rewording, reordering or deleting a line keeps every other line's id and so
 * its key, `content.<fileId>.<lineId>`, and its translation. The file around the records belongs to the dialogue
 * and quest schemas (`docs/backlog/world-systems-map.md`, rows 4 and 5), which embed the record and call the
 * validator; it is not defined here. The placeholder parser is `daedalus-core/words`'s; the derived key check is the
 * game's grammar, `./word-keys.ts`.
 */

import { isPluralValue, parseTemplate } from "daedalus-core/words";
import type { PluralValue, TemplateProblem } from "daedalus-core/words";
import { codePointCount } from "../chat/text.ts";
import { isDerivedKey } from "./word-keys.ts";

/**
 * A line's text: a string with the line variables as placeholders, or a plural object chosen by `{count}`.
 */
export type LineText = string | PluralValue;

/**
 * One line record. `speaker` is the speaking character's id for a spoken line, null for every other text; `note` is
 * for a translator and whoever rewrites the line, never shown to players, and may be empty.
 */
export interface LineRecord {
  readonly id: string;
  readonly speaker: string | null;
  readonly text: LineText;
  readonly note: string;
}

/**
 * The variables a line may hold, closed: the player's two names, an item worded from its nested key, and the count
 * a plural is chosen by. The dialogue and quest designs may add to the set by their own ADRs.
 */
export const LINE_VARIABLES = Object.freeze(["playerFirstName", "playerFamilyName", "item", "count"] as const);

/**
 * One line variable.
 */
export type LineVariable = (typeof LINE_VARIABLES)[number];

/**
 * The highest line number a file may use.
 */
export const MAX_LINE_NUMBER = 999_999;

/**
 * The `nextLine` of a file that has no line yet.
 */
export const FIRST_NEXT_LINE = 1;

/**
 * A line's id: `l` and a number from 1 to `MAX_LINE_NUMBER`, with no leading zero.
 */
export const LINE_ID_PATTERN = /^l[1-9][0-9]{0,5}$/;

/**
 * A speaker's id: lower case, digits, underscores and hyphens, at most 48 characters.
 */
export const SPEAKER_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,47}$/;

/**
 * A content file's id, the key's middle segment: lower case, digits, underscores and hyphens, at most 64
 * characters, so the longest key is 80 and well inside the 160 a `content` key may take.
 */
export const CONTENT_FILE_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The most code points one text, or one form of a plural text, holds.
 */
export const MAX_LINE_TEXT_CHARS = 300;

/**
 * The most code points a note holds.
 */
export const MAX_LINE_NOTE_CHARS = 1000;

/**
 * What the validator refuses, each with the record's position and, once the record's id is known, its id.
 */
export type LineProblem =
  | { readonly kind: "invalidNextLine"; readonly nextLine: unknown }
  | { readonly kind: "notARecord"; readonly index: number }
  | { readonly kind: "invalidId"; readonly index: number; readonly id: unknown }
  | { readonly kind: "duplicateId"; readonly index: number; readonly id: string; readonly first: number }
  | { readonly kind: "idNotBeforeNextLine"; readonly index: number; readonly id: string; readonly nextLine: number }
  | { readonly kind: "invalidSpeaker"; readonly index: number; readonly id: string }
  | { readonly kind: "invalidText"; readonly index: number; readonly id: string }
  | { readonly kind: "emptyText"; readonly index: number; readonly id: string }
  | { readonly kind: "textTooLong"; readonly index: number; readonly id: string; readonly chars: number; readonly max: number }
  | { readonly kind: "unknownVariable"; readonly index: number; readonly id: string; readonly name: string }
  | { readonly kind: "dottedPlaceholder"; readonly index: number; readonly id: string; readonly name: string }
  | { readonly kind: "malformedPlaceholder"; readonly index: number; readonly id: string; readonly problem: Exclude<TemplateProblem, "dottedName">; readonly at: number }
  | { readonly kind: "invalidNote"; readonly index: number; readonly id: string }
  | { readonly kind: "noteTooLong"; readonly index: number; readonly id: string; readonly chars: number; readonly max: number };

/**
 * The record's fields, sorted, which a record holds exactly.
 */
const RECORD_FIELDS: readonly string[] = ["id", "note", "speaker", "text"];

/**
 * The variables as a set.
 */
const VARIABLE_SET: ReadonlySet<string> = new Set<string>(LINE_VARIABLES);

/**
 * The number in a line's id.
 *
 * @param id The id.
 * @returns The number, or null when the id is not a line id.
 */
export function lineNumber(id: string): number | null {
  return LINE_ID_PATTERN.test(id) ? Number(id.slice(1)) : null;
}

/**
 * Whether a value is a file's `nextLine`: a whole number from `FIRST_NEXT_LINE` to one past `MAX_LINE_NUMBER`.
 *
 * @param value Anything.
 * @returns True for a usable `nextLine`.
 */
function isNextLine(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= FIRST_NEXT_LINE && value <= MAX_LINE_NUMBER + 1;
}

/**
 * Takes the next line id of a file, for a line being added.
 *
 * @param nextLine The file's `nextLine`.
 * @returns The new line's id and the file's `nextLine` after it.
 * @throws RangeError When `nextLine` is not a whole number from 1, or the file has used every number.
 */
export function takeLineId(nextLine: number): { readonly id: string; readonly nextLine: number } {
  if (!isNextLine(nextLine) || nextLine > MAX_LINE_NUMBER) {
    throw new RangeError(`nextLine ${nextLine} gives no line id: it must be a whole number from ${FIRST_NEXT_LINE} to ${MAX_LINE_NUMBER}`);
  }
  return { id: `l${nextLine}`, nextLine: nextLine + 1 };
}

/**
 * A line's key: `content.<fileId>.<lineId>`.
 *
 * @param fileId The embedding file's id.
 * @param lineId The line's id.
 * @returns The derived key.
 * @throws RangeError When the file id or the line id is not one the rule admits.
 */
export function lineKey(fileId: string, lineId: string): string {
  if (!CONTENT_FILE_ID_PATTERN.test(fileId)) {
    throw new RangeError(`"${fileId}" is not a content file id`);
  }
  if (!LINE_ID_PATTERN.test(lineId)) {
    throw new RangeError(`"${lineId}" is not a line id`);
  }
  const key = `content.${fileId}.${lineId}`;
  if (!isDerivedKey(key)) {
    throw new RangeError(`"${key}" is not a derived key`);
  }
  return key;
}

/**
 * Whether a value is a plain object holding exactly the record's four fields.
 *
 * @param value Anything.
 * @returns True when the shape is a record's; the fields' values are checked apart.
 */
function hasRecordShape(value: unknown): value is { readonly [field: string]: unknown } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const fields = Object.keys(value).sort();
  return fields.length === RECORD_FIELDS.length && fields.every((field, at) => field === RECORD_FIELDS[at]);
}

/**
 * The problems of one text: empty, too long, a malformed or dotted placeholder, or a variable outside the set.
 *
 * @param text The text, or one form of a plural text.
 * @param index The record's position.
 * @param id The record's id.
 * @returns Its problems, in that order; a text that does not parse has only its first parse problem checked.
 */
function textProblems(text: string, index: number, id: string): readonly LineProblem[] {
  const problems: LineProblem[] = [];
  if (text === "") {
    problems.push({ kind: "emptyText", index, id });
  }
  const chars = codePointCount(text);
  if (chars > MAX_LINE_TEXT_CHARS) {
    problems.push({ kind: "textTooLong", index, id, chars, max: MAX_LINE_TEXT_CHARS });
  }
  const parsed = parseTemplate(text);
  if (!parsed.ok) {
    if (parsed.problem === "dottedName") {
      problems.push({ kind: "dottedPlaceholder", index, id, name: parsed.name });
    } else {
      problems.push({ kind: "malformedPlaceholder", index, id, problem: parsed.problem, at: parsed.at });
    }
    return problems;
  }
  const seen = new Set<string>();
  for (const part of parsed.parts) {
    if ("name" in part && !VARIABLE_SET.has(part.name) && !seen.has(part.name)) {
      seen.add(part.name);
      problems.push({ kind: "unknownVariable", index, id, name: part.name });
    }
  }
  return problems;
}

/**
 * Validates a file's line records against its `nextLine`. Each record is an object with exactly `id`, `speaker`,
 * `text` and `note`; its id is a line id, unique in the file and below `nextLine`; its speaker is a speaker's id
 * or null; its text is a non-empty string, or a plural object holding `other`, each form at most
 * `MAX_LINE_TEXT_CHARS` code points with only the line variables as placeholders; and its note is a string of at
 * most `MAX_LINE_NOTE_CHARS` code points.
 *
 * @param lines The records, as read from the file.
 * @param nextLine The file's `nextLine`.
 * @returns Every problem found, in record order; empty when the lines are valid.
 */
export function lineProblems(lines: readonly unknown[], nextLine: unknown): readonly LineProblem[] {
  const problems: LineProblem[] = [];
  const nextLineValid = isNextLine(nextLine);
  if (!nextLineValid) {
    problems.push({ kind: "invalidNextLine", nextLine });
  }
  const firstIndex = new Map<string, number>();
  lines.forEach((line, index) => {
    if (!hasRecordShape(line)) {
      problems.push({ kind: "notARecord", index });
      return;
    }
    const id = line["id"];
    if (typeof id !== "string" || !LINE_ID_PATTERN.test(id)) {
      problems.push({ kind: "invalidId", index, id });
      return;
    }
    const first = firstIndex.get(id);
    if (first !== undefined) {
      problems.push({ kind: "duplicateId", index, id, first });
    } else {
      firstIndex.set(id, index);
    }
    if (nextLineValid && Number(id.slice(1)) >= nextLine) {
      problems.push({ kind: "idNotBeforeNextLine", index, id, nextLine });
    }
    const speaker = line["speaker"];
    if (speaker !== null && (typeof speaker !== "string" || !SPEAKER_ID_PATTERN.test(speaker))) {
      problems.push({ kind: "invalidSpeaker", index, id });
    }
    const text = line["text"];
    if (typeof text === "string") {
      problems.push(...textProblems(text, index, id));
    } else if (isPluralValue(text) && text.other !== undefined) {
      for (const form of Object.values(text)) {
        problems.push(...textProblems(form, index, id));
      }
    } else {
      problems.push({ kind: "invalidText", index, id });
    }
    const note = line["note"];
    if (typeof note !== "string") {
      problems.push({ kind: "invalidNote", index, id });
    } else {
      const chars = codePointCount(note);
      if (chars > MAX_LINE_NOTE_CHARS) {
        problems.push({ kind: "noteTooLong", index, id, chars, max: MAX_LINE_NOTE_CHARS });
      }
    }
  });
  return problems;
}
